import {
    ICharacterProperties,
    IContentLine,
    OneOfContentSpans,
    OneOfEditableSpans,
    PreviousStyleIdType,
    TextDirection
} from '@studio/domain';
import { Observable } from 'rxjs';
import { TextEvents } from '@app/design-view/services/editor-event/text-events';
import { CreativeDataNode } from '../../nodes/base-data-node';
import { TextSelection } from './rich-text.selection.text';
import { IElement } from '@studio/domain/creativeset/element';
import { IVersion, IVersionedText, IVersionProperty } from '@studio/domain/creativeset/version';

export interface ILineColumnPosition {
    line: number;
    column: number;
}

export interface ILineColumnPositionDirection extends ILineColumnPosition {
    dir: TextDirection.Ltr | TextDirection.Rtl;
}

export interface ICaretRange {
    start: ILineColumnPosition;
    end: ILineColumnPosition;
    isCollapsed: boolean;
    getRangeLength(columnLengths: number[]): number;
}

export interface ITextPosition {
    node: Node;
    offset: number;
    dir: TextDirection.Ltr | TextDirection.Rtl;
    correctOffset?: boolean /* Due to caretRangeFromPoint is setting wrong offset when selecting lastline, we need to know when we should correct it */;
}

// CharacterOffset is the offset of a user input. It could be either a character
// or just a newline. We need to keep track of this to know where the caret should
// be located.
export const enum CharacterOffset {
    Character,
    Newline
}

export class CaretRange implements ICaretRange {
    get isCollapsed(): boolean {
        return this.start.line === this.end.line && this.start.column === this.end.column;
    }

    getRangeLength(numberOfColumns: number[]): number {
        if (this.start.line === this.end.line) {
            return this.end.column - this.start.column;
        }

        let length = numberOfColumns[0] - this.start.column;
        for (let i = this.start.line + 1; i < this.end.line; i++) {
            length += numberOfColumns[i];
        }
        length += this.end.column;
        return length;
    }

    constructor(
        public start: ILineColumnPosition,
        public end: ILineColumnPosition
    ) {}
}

export interface ISaveSnapshotOption {
    isVersionable?: boolean;
    hasChanged?: boolean;
}

export interface IHistoryService {
    createSnapshotCandidate(): void;
    saveLastSnapshotCandidate(option?: ISaveSnapshotOption): void;
    throttleUndoSnapshot(): void;
}

export interface IEditorState {
    currentVersion: IVersion;
    document: CreativeDataNode;
    elements: IElement[];
    versions: IVersion[];
    zoom: number;
    updateVersionedText(
        versionId: string,
        versionPropertyId: string,
        value: Partial<IVersionedText>
    ): void;
    updateVersionProperty(versionId: string, versionProperty: IVersionProperty): void;
}

export interface IEditorEvent {
    designViewInit$: Observable<boolean>;
    renderedCanvas$: Observable<void>;
    timelineInit$: Observable<void>;
    workspaceViewInit$: Observable<void>;
    workspaceInit$: Observable<boolean>;
    text: TextEvents;
}

export interface ISubject<T> {
    next(value: T): void;
}

export interface INodePosition {
    node: Node;
    startOffset: number;
    endOffset: number;
}

export interface IContentSpanSplit {
    preContentSpan: OneOfContentSpans;
    postContentSpan: OneOfContentSpans;
}

export interface IPreviousStyle {
    styleIds: (string | PreviousStyleIdType)[];
    styleIdToHistoryIndexMap: Map<string /* styleId */, number /* historyIndex */>;
}

export interface IClientPosition {
    clientX: number;
    clientY: number;
}

export interface ISnapshot {
    currentStyle: Partial<ICharacterProperties>;
    spans: OneOfEditableSpans[];
    textLines: IContentLine[];
    selection?: TextSelection;
}
